import { ThemeOptions } from '@mui/material/styles';
import { nordicLogoThemeLight, nordicLogoThemeDark } from '../logoConfig';

// Create a function to generate base theme to avoid object reuse
const createBaseTheme = (): Partial<ThemeOptions> => ({
  palette: {
    primary: {
      main: '#697A89',
      light: '#93A3B1',
      dark: '#465561',
    },
    secondary: {
      main: '#8B7E74',
      light: '#B3A59B',
      dark: '#625650',
    },
    success: {
      main: '#789F8A',
      light: '#9FBFAC',
      dark: '#527061',
    },
    warning: {
      main: '#C8A882',
      light: '#DBC3A3',
      dark: '#9E7E5B',
    },
    error: {
      main: '#B08080',
      light: '#C9A3A3',
      dark: '#875D5D',
    },
    info: {
      main: '#7B9AAE',
      light: '#9FB7C7',
      dark: '#576F82',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 300,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 300,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 400,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
    },
  },
  shape: {
    borderRadius: 4,
  },
});

const baseTheme = createBaseTheme();

export const nordicMinimalLight: ThemeOptions = {
  ...baseTheme,
  logo: nordicLogoThemeLight,
  palette: {
    ...baseTheme.palette,
    mode: 'light',
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    text: {
      primary: '#424242',
      secondary: '#616161',
      disabled: '#9E9E9E',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    action: {
      active: '#424242',
      hover: 'rgba(105, 122, 137, 0.04)',
      selected: 'rgba(105, 122, 137, 0.08)',
      disabled: '#BDBDBD',
      disabledBackground: 'rgba(0, 0, 0, 0.06)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 400,
          letterSpacing: '0.02em',
          boxShadow: 'none',
          borderRadius: 4,
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: 'rgba(105, 122, 137, 0.08)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.04)',
          '&.MuiPaper-elevation1': {
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.04)',
          },
          '&.MuiPaper-elevation2': {
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.06)',
          },
          '&.MuiPaper-elevation3': {
            boxShadow: '0px 3px 9px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
};

export const nordicMinimalDark: ThemeOptions = {
  ...baseTheme,
  logo: nordicLogoThemeDark,
  palette: {
    ...baseTheme.palette,
    mode: 'dark',
    primary: {
      main: '#8A9BA8',
      light: '#AAB8C2',
      dark: '#6A7B88',
    },
    secondary: {
      main: '#A69990',
      light: '#C2B3A9',
      dark: '#7F7068',
    },
    background: {
      default: '#1A1D20',
      paper: '#222528',
    },
    grey: {
      50: '#2C2F32',
      100: '#353739',
      200: '#3E4043',
      300: '#4A4C4F',
      400: '#5C5E61',
      500: '#717375',
      600: '#8B8D90',
      700: '#A1A3A6',
      800: '#B8BABC',
      900: '#D1D3D4',
    },
    text: {
      primary: '#E8EAEC',
      secondary: '#B8BABC',
      disabled: '#717375',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
    action: {
      active: '#E8EAEC',
      hover: 'rgba(138, 155, 168, 0.08)',
      selected: 'rgba(138, 155, 168, 0.16)',
      disabled: '#5C5E61',
      disabledBackground: 'rgba(255, 255, 255, 0.06)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 400,
          letterSpacing: '0.02em',
          boxShadow: 'none',
          borderRadius: 4,
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: 'rgba(138, 155, 168, 0.12)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
          '&.MuiPaper-elevation1': {
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
          },
          '&.MuiPaper-elevation2': {
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.25)',
          },
          '&.MuiPaper-elevation3': {
            boxShadow: '0px 3px 9px rgba(0, 0, 0, 0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
  },
};